/**
 * Error message patterns Sentry should ignore when deciding whether to report
 * an error.
 *
 * Sentry evaluates `ignoreErrors` against each event's error message — an event
 * is dropped when any message matches any entry in the list.
 */
export const sentryIgnoreErrors: (string | RegExp)[] = [
  // Filter out noise from Facebook's in-app browser (IAB) on Android. When the
  // IAB closes, Facebook's injected navigation_performance_logger_android script
  // fires a teardown handler after the Java bridge has already been released,
  // causing an unhandled "Java object is gone" error that originates entirely
  // within Facebook's own injected code and does not reflect any issue in our
  // application.
  /Java object is gone/,
  /Error invoking postMessage/,
  // Filter out noise from Facebook's in-app browser (IAB) on iOS. When the IAB
  // closes or navigates away, Facebook's injected script attempts to call
  // window.webkit.messageHandlers to communicate with the native iOS layer.
  // If the WebKit message handler bridge is unavailable at that point,
  // a TypeError is thrown that originates entirely within Facebook's own
  // injected code and does not reflect any issue in our application.
  /window\.webkit\.messageHandlers/,
  // Accounts can configure their own Google Tag Manager container on public
  // report pages (see InitialiseGtm.ts). That container is managed entirely
  // by the account, and sometimes contains a Facebook Pixel tag that calls
  // the global `fbq` function without first loading Facebook's base pixel
  // code (fbevents.js) — e.g. due to tag sequencing or an ad-blocker. The
  // resulting ReferenceError originates entirely within the account's own
  // GTM configuration, not our application. Sentry's `denyUrls` for
  // googletagmanager.com does not catch this case because the innermost
  // frame Sentry resolves the URL from is its own bundled instrumentation
  // wrapper, not gtm.js, so we filter on the message instead.
  /Can't find variable: fbq/,
  /fbq is not defined/,
  // A lapsed session (401) or expired CSRF token (419) answers every
  // in-flight background request at once. The user cannot act on it and
  // there is nothing to fix, so it is noise wherever it surfaces —
  // including as an unhandled rejection from a request whose caller does
  // not catch (the address/InstantSearch typeahead). Filtering centrally
  // replaces the global axios interceptor this app used to rely on.
  /Request failed with status 401/,
  /Request failed with status 419/,
];
